/**
 * GateModeScreen — §0b
 * Continuous ANPR scanning via iPhone rear camera.
 * Frames are captured at 640×360, encoded as base64 JPEG,
 * and POSTed to /api/gate-mode/scan. The API key never
 * touches the frontend.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Camera, CameraOff, X, AlertTriangle, CheckCircle, AlertCircle } from 'lucide-react';

const SCAN_INTERVAL_MS = 3000;
const DEDUP_WINDOW_MS = 30000;
const CAPTURE_MAX_WIDTH = 1280; // cap sent to Plate Recognizer — maintains aspect ratio
const JPEG_QUALITY = 0.7;
const LOG_MAX = 10;

// ─── Audio ────────────────────────────────────────────────────────────────────

function playAlertTone(level) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const gain = ctx.createGain();
    gain.connect(ctx.destination);

    if (level >= 4) {
      // Urgent alternating tone for Level 4-5
      [0, 0.3, 0.6].forEach((offset, i) => {
        const osc = ctx.createOscillator();
        osc.type = 'square';
        osc.frequency.value = i % 2 === 0 ? 880 : 660;
        osc.connect(gain);
        gain.gain.setValueAtTime(0.4, ctx.currentTime + offset);
        gain.gain.setValueAtTime(0, ctx.currentTime + offset + 0.25);
        osc.start(ctx.currentTime + offset);
        osc.stop(ctx.currentTime + offset + 0.25);
      });
    } else {
      // Single beep for Level 3
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = 440;
      osc.connect(gain);
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.4);
    }
  } catch (_) {
    // Web Audio not available — silent fail
  }
}

// ─── Push notification ────────────────────────────────────────────────────────

async function sendNotification(vrn, threatLabel) {
  if (!('Notification' in window)) return;
  if (Notification.permission === 'granted') {
    new Notification('⚠ VCIN Gate Mode Alert', {
      body: `${vrn} — ${threatLabel}. Take action immediately.`,
      icon: '/logo192.png',
      tag: `gate-mode-${vrn}`,
      requireInteraction: true,
    });
  }
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const S = {
  screen: {
    minHeight: '100vh',
    background: '#0a0a0a',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: "'Inter', sans-serif",
    color: 'white',
    overflow: 'hidden',
    position: 'relative',
  },
  header: {
    background: 'rgba(255,255,255,0.05)',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexShrink: 0,
    zIndex: 10,
  },
  headerTitle: {
    fontFamily: "'Oswald', sans-serif",
    fontSize: '14px',
    fontWeight: 600,
    letterSpacing: '3px',
    textTransform: 'uppercase',
    color: 'white',
    flex: 1,
  },
  siteName: {
    fontSize: '11px',
    color: 'rgba(255,255,255,0.5)',
    letterSpacing: '1px',
  },
  stopBtn: {
    background: 'rgba(220,38,38,0.2)',
    border: '1px solid rgba(220,38,38,0.4)',
    borderRadius: '8px',
    padding: '6px 12px',
    color: '#f87171',
    fontFamily: "'Oswald', sans-serif",
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '2px',
    textTransform: 'uppercase',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  cameraWrap: {
    flex: '0 0 58vh',
    background: '#111',
    position: 'relative',
    overflow: 'hidden',
  },
  video: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  scanLine: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: 'rgba(74,122,255,0.7)',
    animation: 'scanPulse 2s ease-in-out infinite',
  },
  cameraOff: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    gap: '12px',
    color: 'rgba(255,255,255,0.3)',
  },
  resultPanel: {
    flexShrink: 0,
    padding: '12px 16px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
  },
  plateBadge: (threatLevel) => ({
    background: threatLevel >= 4 ? '#dc2626' : threatLevel === 3 ? '#d97706' : '#166534',
    border: `1px solid ${threatLevel >= 4 ? '#ef4444' : threatLevel === 3 ? '#f59e0b' : '#22c55e'}`,
    borderRadius: '10px',
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  }),
  plateText: {
    fontFamily: "'Oswald', sans-serif",
    fontSize: '22px',
    fontWeight: 700,
    letterSpacing: '5px',
    background: '#F5C400',
    color: '#111',
    borderRadius: '4px',
    padding: '2px 8px',
  },
  resultLabel: {
    flex: 1,
  },
  resultTitle: {
    fontFamily: "'Oswald', sans-serif",
    fontSize: '14px',
    fontWeight: 600,
    letterSpacing: '2px',
    textTransform: 'uppercase',
  },
  resultSub: {
    fontSize: '11px',
    opacity: 0.7,
    marginTop: '2px',
  },
  logPanel: {
    flex: 1,
    overflow: 'hidden',
    padding: '10px 16px',
  },
  logLabel: {
    fontFamily: "'Oswald', sans-serif",
    fontSize: '9px',
    fontWeight: 600,
    letterSpacing: '3px',
    color: 'rgba(255,255,255,0.3)',
    textTransform: 'uppercase',
    marginBottom: '8px',
  },
  logList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    overflow: 'hidden',
  },
  logItem: (threatLevel) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '5px 8px',
    background: 'rgba(255,255,255,0.04)',
    borderRadius: '6px',
    borderLeft: `2px solid ${threatLevel >= 4 ? '#dc2626' : threatLevel >= 3 ? '#d97706' : '#22c55e'}`,
  }),
  logPlate: {
    fontFamily: "'Oswald', sans-serif",
    fontSize: '13px',
    fontWeight: 600,
    letterSpacing: '2px',
    flex: 1,
  },
  logTime: {
    fontSize: '10px',
    color: 'rgba(255,255,255,0.3)',
  },
  // Signage confirmation overlay
  signageOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.92)',
    zIndex: 100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
  },
  signageCard: {
    background: '#1a1a2e',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '16px',
    padding: '28px 24px',
    maxWidth: '340px',
    width: '100%',
  },
  // Level 4-5 takeover overlay
  takeoverOverlay: {
    position: 'fixed',
    inset: 0,
    background: '#7f1d1d',
    zIndex: 200,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '32px 24px',
    textAlign: 'center',
  },
};

// ─── Scan log entry ───────────────────────────────────────────────────────────

function LogEntry({ entry }) {
  const icon = entry.threatLevel >= 4
    ? <AlertTriangle size={14} color="#ef4444" />
    : entry.threatLevel >= 3
    ? <AlertCircle size={14} color="#f59e0b" />
    : <CheckCircle size={14} color="#22c55e" />;

  return (
    <div style={S.logItem(entry.threatLevel)}>
      {icon}
      <span style={S.logPlate}>{entry.vrn}</span>
      <span style={S.logTime}>{entry.time}</span>
    </div>
  );
}

// ─── Main screen ─────────────────────────────────────────────────────────────

export default function GateModeScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // iOS Safari silently refuses to render live video to canvas unless these
  // three attributes are set as real DOM attributes — React props alone are
  // not reliable enough. The ref callback forces them the moment the element mounts.
  const videoRef = useRef(null);
  const videoRefCallback = useCallback((el) => {
    if (!el) return;
    el.setAttribute('playsinline', '');
    el.setAttribute('muted', '');
    el.setAttribute('autoplay', '');
    videoRef.current = el;
  }, []);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const intervalRef = useRef(null);
  const dedupRef = useRef(new Map()); // vrn → timestamp

  const [scanning, setScanning] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [signageConfirmed, setSignageConfirmed] = useState(false);
  const [showSignagePrompt, setShowSignagePrompt] = useState(true);
  const [signageChecked, setSignageChecked] = useState(false);

  const [lastResult, setLastResult] = useState(null); // { vrn, threatLevel, label }
  const [log, setLog] = useState([]);
  const [takeover, setTakeover] = useState(null); // { vrn, threatLevel, label, action, sightings }
  const [debugFrameBytes, setDebugFrameBytes] = useState(null); // TEMP
  const [debugDataUrl, setDebugDataUrl] = useState(null);       // TEMP: thumbnail of last captured frame

  // Derive site from officer's first assigned site
  const siteId = user?.assigned_sites?.[0]?.id || null;
  const siteName = user?.assigned_sites?.[0]?.name || 'Gate';

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Stop camera on unmount
  useEffect(() => {
    return () => stopScanning();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        // Wait for the video to have actual frame data before capturing.
        // readyState < 2 (HAVE_CURRENT_DATA) means the canvas will draw a
        // black frame, producing a ~4.5KB blob that wastes a PR API call.
        if (videoRef.current.readyState < 2) {
          await new Promise((resolve, reject) => {
            const onReady = () => { videoRef.current?.removeEventListener('loadeddata', onReady); resolve(); };
            const onError = () => { videoRef.current?.removeEventListener('error', onError); reject(new Error('Video error')); };
            videoRef.current.addEventListener('loadeddata', onReady);
            videoRef.current.addEventListener('error', onError);
          });
        }
      }
      setCameraError(null);
      return true;
    } catch (err) {
      setCameraError(err.name === 'NotAllowedError'
        ? 'Camera permission denied. Allow camera access to use Gate Mode.'
        : 'Camera unavailable. Ensure this device has a rear camera.');
      return false;
    }
  }, []);

  const stopScanning = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setScanning(false);
  }, []);

  const captureAndScan = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) return;
    // readyState < 2 → no frame data yet
    if (videoRef.current.readyState < 2) return;
    // videoWidth === 0 means iOS hasn't decoded a real frame yet — canvas
    // would draw black. This is the key iOS Safari guard.
    if (videoRef.current.videoWidth === 0) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    // Scale down to CAPTURE_MAX_WIDTH if the native resolution is larger,
    // preserving aspect ratio. Keeps payloads at ~50-80 KB instead of 196 KB+.
    const scale = Math.min(1, CAPTURE_MAX_WIDTH / video.videoWidth);
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
    const base64 = dataUrl.split(',')[1];
    if (!base64) return;

    // TEMP: surface frame for on-device debugging
    setDebugFrameBytes(Math.round(base64.length * 0.75)); // base64 → approx bytes
    setDebugDataUrl(dataUrl); // thumbnail — lets us see exactly what the canvas captured

    // A real frame at this resolution is typically 20–80 KB.
    // Anything under 15 KB is a blank/black frame — skip it.
    if (base64.length < 20000) return;

    let result;
    try {
      const response = await api.post('/gate-mode/scan', {
        image: base64,
        site_id: siteId,
      });
      result = response.data;
      console.log('[GateMode] scan response | vrn:', result.vrn, '| match:', result.match, '| threat_level:', result.threat_level ?? 'n/a', '| message:', result.message ?? 'none');
    } catch (err) {
      const status = err?.response?.status ?? err?.statusCode ?? 'no-status';
      const errBody = err?.response?.data ?? err?.data ?? {};
      console.log('[GateMode] scan request failed | status:', status, '| error:', errBody.error ?? 'unknown', '| message:', errBody.message ?? err?.message ?? 'unknown');
      return;
    }

    if (!result.vrn) {
      console.log('[GateMode] no plate detected this frame');
      return; // No plate detected
    }

    const vrn = result.vrn;

    // §0b constraint 4: dedup — skip if same plate seen within 30 seconds
    const now = Date.now();
    const lastSeen = dedupRef.current.get(vrn);
    if (lastSeen && now - lastSeen < DEDUP_WINDOW_MS) return;
    dedupRef.current.set(vrn, now);

    const threatLevel = result.threat_level || 0;
    const label = result.threat_label || (result.known_vehicle?.status === 'BANNED' ? 'BANNED' : 'Clear');
    const time = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

    // Update last result display
    setLastResult({ vrn, threatLevel, label, action: result.action_required });

    // Add to log (newest first, capped at LOG_MAX)
    setLog(prev => [{ vrn, threatLevel, label, time }, ...prev].slice(0, LOG_MAX));

    // Level 3+: audio alert
    if (threatLevel >= 3 || result.known_vehicle?.status === 'BANNED') {
      playAlertTone(threatLevel >= 4 ? 4 : 3);
    }

    // Level 4-5: full screen takeover + push notification
    if (threatLevel >= 4) {
      setTakeover({
        vrn,
        threatLevel,
        label,
        action: result.action_required,
        sightings: result.national_sightings || 0,
      });
      sendNotification(vrn, label);
    }
  }, [siteId]);

  const handleStart = useCallback(async () => {
    // Clear any existing interval before starting — prevents duplicate loops
    // if handleStart is ever called while a scan is already running.
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    const ok = await startCamera();
    if (!ok) return;
    setScanning(true);
    intervalRef.current = setInterval(captureAndScan, SCAN_INTERVAL_MS);
  }, [startCamera, captureAndScan]);

  const handleConfirmSignage = useCallback(() => {
    if (!signageChecked) return;
    setSignageConfirmed(true);
    setShowSignagePrompt(false);
    handleStart();
  }, [signageChecked, handleStart]);

  const handleStop = useCallback(() => {
    stopScanning();
    navigate('/');
  }, [stopScanning, navigate]);

  const dismissTakeover = useCallback(() => {
    setTakeover(null);
  }, []);

  // ── Render: signage confirmation ──────────────────────────────────────────

  if (showSignagePrompt && !signageConfirmed) {
    return (
      <div style={S.screen}>
        <div style={S.signageOverlay}>
          <div style={S.signageCard}>
            <div style={{ marginBottom: '16px' }}>
              <Camera size={32} color="#4a7aff" style={{ marginBottom: '12px' }} />
              <div style={{ fontFamily: "'Oswald', sans-serif", fontSize: '18px', fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '8px' }}>
                GATE MODE
              </div>
              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.6 }}>
                Before activating, you must confirm that ANPR signage is displayed at this site entrance, informing visitors that vehicles will be checked against the VCIN national intelligence database.
              </div>
            </div>

            <div style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '10px',
              padding: '12px',
              marginBottom: '20px',
              fontSize: '11px',
              color: 'rgba(255,255,255,0.5)',
              lineHeight: 1.6,
            }}>
              <strong style={{ color: 'rgba(255,255,255,0.8)' }}>Required signage text:</strong><br />
              "NOTICE — VEHICLE INTELLIGENCE MONITORING. This site uses ANPR. Vehicle registrations are checked against the VCIN national security intelligence database."
            </div>

            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', marginBottom: '20px' }}>
              <input
                type="checkbox"
                checked={signageChecked}
                onChange={e => setSignageChecked(e.target.checked)}
                style={{ marginTop: '2px', accentColor: '#4a7aff', width: '16px', height: '16px', flexShrink: 0 }}
              />
              <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)', lineHeight: 1.5 }}>
                I confirm signage is displayed at this site
              </span>
            </label>

            <button
              onClick={handleConfirmSignage}
              disabled={!signageChecked}
              style={{
                width: '100%',
                background: signageChecked ? '#4a7aff' : 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: '10px',
                padding: '14px',
                color: signageChecked ? 'white' : 'rgba(255,255,255,0.3)',
                fontFamily: "'Oswald', sans-serif",
                fontSize: '14px',
                fontWeight: 600,
                letterSpacing: '3px',
                textTransform: 'uppercase',
                cursor: signageChecked ? 'pointer' : 'not-allowed',
                marginBottom: '10px',
              }}
            >
              Activate Gate Mode
            </button>

            <button
              onClick={() => navigate('/')}
              style={{
                width: '100%',
                background: 'transparent',
                border: 'none',
                padding: '10px',
                color: 'rgba(255,255,255,0.35)',
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: Level 4-5 takeover ────────────────────────────────────────────

  if (takeover) {
    return (
      <div style={S.takeoverOverlay}>
        <div style={{
          fontSize: '48px',
          marginBottom: '16px',
          animation: 'pulse 1s ease-in-out infinite',
        }}>⚠</div>

        <div style={{
          fontFamily: "'Oswald', sans-serif",
          fontSize: '16px',
          fontWeight: 700,
          letterSpacing: '4px',
          color: '#fca5a5',
          textTransform: 'uppercase',
          marginBottom: '20px',
        }}>
          HIGH THREAT VEHICLE
        </div>

        <div style={{
          background: '#F5C400',
          color: '#111',
          fontFamily: "'Oswald', sans-serif",
          fontSize: '36px',
          fontWeight: 700,
          letterSpacing: '8px',
          borderRadius: '8px',
          padding: '8px 20px',
          border: '4px solid #111',
          marginBottom: '16px',
        }}>
          {takeover.vrn}
        </div>

        <div style={{
          fontFamily: "'Oswald', sans-serif",
          fontSize: '18px',
          fontWeight: 700,
          letterSpacing: '2px',
          color: 'white',
          textTransform: 'uppercase',
          marginBottom: '6px',
        }}>
          LEVEL {takeover.threatLevel} — {takeover.label.toUpperCase()}
        </div>

        {takeover.sightings > 0 && (
          <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', marginBottom: '28px' }}>
            {takeover.sightings} national sighting{takeover.sightings !== 1 ? 's' : ''}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%', maxWidth: '280px' }}>
          <a
            href="tel:999"
            style={{
              display: 'block',
              background: '#dc2626',
              border: '2px solid #ef4444',
              borderRadius: '12px',
              padding: '16px',
              color: 'white',
              fontFamily: "'Oswald', sans-serif",
              fontSize: '16px',
              fontWeight: 700,
              letterSpacing: '3px',
              textTransform: 'uppercase',
              textAlign: 'center',
              textDecoration: 'none',
            }}
          >
            CALL 999
          </a>

          <button
            onClick={() => {
              dismissTakeover();
              navigate('/log-sighting', { state: { prefillVrn: takeover.vrn } });
            }}
            style={{
              background: 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '12px',
              padding: '14px',
              color: 'white',
              fontFamily: "'Oswald', sans-serif",
              fontSize: '14px',
              fontWeight: 600,
              letterSpacing: '2px',
              textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >
            LOG SIGHTING
          </button>

          <button
            onClick={dismissTakeover}
            style={{
              background: 'transparent',
              border: 'none',
              padding: '12px',
              color: 'rgba(255,255,255,0.5)',
              fontSize: '13px',
              cursor: 'pointer',
            }}
          >
            Dismiss
          </button>
        </div>
      </div>
    );
  }

  // ── Render: main scanning UI ──────────────────────────────────────────────

  const lastThreatLevel = lastResult?.threatLevel || 0;
  const lastLabel = lastResult ? (lastResult.label || 'Clear') : null;

  return (
    <div style={S.screen}>
      <style>{`
        @keyframes scanPulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>

      {/* Hidden canvas for frame capture */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />


      {/* Header */}
      <div style={S.header}>
        <Camera size={16} color="rgba(255,255,255,0.6)" />
        <div style={{ flex: 1 }}>
          <div style={S.headerTitle}>VCIN Gate Mode</div>
          <div style={S.siteName}>{siteName}</div>
        </div>
        <button style={S.stopBtn} onClick={handleStop}>
          <X size={12} />
          Stop
        </button>
      </div>

      {/* Camera feed */}
      <div style={S.cameraWrap}>
        {cameraError ? (
          <div style={S.cameraOff}>
            <CameraOff size={40} />
            <div style={{ fontSize: '13px', textAlign: 'center', maxWidth: '260px' }}>{cameraError}</div>
          </div>
        ) : (
          <>
            <video
              ref={videoRefCallback}
              style={S.video}
              playsInline
              muted
              autoPlay
            />
            {scanning && <div style={S.scanLine} />}
          </>
        )}
      </div>

      {/* TEMP: canvas capture debug — thumbnail + byte size. Remove after PR confirmed. */}
      {scanning && (
        <div style={{
          background: '#0a1a0a', borderBottom: '1px solid #1a3a1a',
          padding: '6px 12px', flexShrink: 0,
          display: 'flex', alignItems: 'center', gap: '10px',
        }}>
          {debugDataUrl && (
            <img
              src={debugDataUrl}
              alt="last captured frame"
              style={{ width: '150px', height: 'auto', borderRadius: '4px', border: '1px solid #1a3a1a' }}
            />
          )}
          <div style={{ fontSize: '11px', fontFamily: 'monospace', color: '#66ff66' }}>
            {debugFrameBytes === null
              ? 'Waiting for first frame…'
              : debugFrameBytes < 15000
                ? `${debugFrameBytes.toLocaleString()} B ⚠ BLANK`
                : `${debugFrameBytes.toLocaleString()} B ✓`
            }
          </div>
        </div>
      )}

      {/* Result panel */}
      <div style={S.resultPanel}>
        {lastResult ? (
          <div style={S.plateBadge(lastThreatLevel)}>
            <span style={S.plateText}>{lastResult.vrn}</span>
            <div style={S.resultLabel}>
              <div style={S.resultTitle}>{lastLabel}</div>
              {lastResult.action && (
                <div style={S.resultSub}>{lastResult.action}</div>
              )}
            </div>
            {lastThreatLevel >= 4
              ? <AlertTriangle size={20} color="#ef4444" />
              : lastThreatLevel >= 3
              ? <AlertCircle size={20} color="#f59e0b" />
              : <CheckCircle size={20} color="#22c55e" />}
          </div>
        ) : (
          <div style={{
            padding: '12px',
            textAlign: 'center',
            fontSize: '12px',
            color: 'rgba(255,255,255,0.3)',
            fontFamily: "'Oswald', sans-serif",
            letterSpacing: '2px',
            textTransform: 'uppercase',
          }}>
            {scanning ? 'Scanning...' : 'Waiting'}
          </div>
        )}
      </div>

      {/* Scrolling scan log */}
      <div style={S.logPanel}>
        <div style={S.logLabel}>Recent scans</div>
        <div style={S.logList}>
          {log.length === 0 ? (
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.2)' }}>No plates detected yet</div>
          ) : (
            log.map((entry, i) => <LogEntry key={`${entry.vrn}-${i}`} entry={entry} />)
          )}
        </div>
      </div>
    </div>
  );
}
